import React, { useState, useEffect } from 'react';
import CustomAlert from '../components/CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';
import { 
  getAllTutors, 
  addTutor, 
  addTutorsBatch, 
  updateTutor, 
  deleteTutorCascade,
  issueTemporaryPassword
} from '../services/firestoreService';
import '../styles/TutorManagement.css';

const TutorManagement = ({ user }) => {
  const { alertState, showSuccess, showError, showWarning, showConfirm, showInfo } = useCustomAlert();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [allTutors, setAllTutors] = useState([]); // 전체 튜터 목록 (검색용)
  const [deletingId, setDeletingId] = useState(null);

  // 단일 튜터 등록 폼
  const [newTutor, setNewTutor] = useState({
    name: '',
    email: '',
    phone: '',
    hasCompetition: false // 경시/올림피아드 이력
  });

  // 일괄 등록 데이터
  const [batchData, setBatchData] = useState({
    names: '',
    emails: '',
    phones: '',
    competitions: ''
  });



  const tutorsPerPage = 10;



  // 전화번호 포맷팅 함수 (010-1234-5678 형식으로 변환)
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '');
    
    // 11자리 숫자인 경우에만 포맷팅
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    
    // 10자리 숫자인 경우 (010으로 시작하는 경우)
    if (numbers.length === 10 && numbers.startsWith('010')) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    
    // 그 외의 경우는 원본 반환
    return phone;
  };

  // 컴포넌트 마운트 시 튜터 목록 로드
  useEffect(() => {
    loadTutors();
  }, []);

  // 튜터 목록 로드 (실제 Firestore에서 가져오기)
  const loadTutors = async () => {
    setLoading(true);
    setError(null);
    try {
      // 실제 Firestore에서 튜터 목록 가져오기
      const tutorList = await getAllTutors();
      setAllTutors(tutorList); // 전체 목록 저장
      
      // 검색어가 있으면 필터링
      const filteredTutors = searchTerm.trim() 
        ? tutorList.filter(tutor => 
            tutor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tutor.email.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : tutorList;
      
      // 페이지네이션 적용
      const startIndex = (currentPage - 1) * tutorsPerPage;
      const endIndex = startIndex + tutorsPerPage;
      const paginatedTutors = filteredTutors.slice(startIndex, endIndex);
      setTutors(paginatedTutors);
      setTotalPages(Math.ceil(filteredTutors.length / tutorsPerPage));
    } catch (error) {
      console.error('튜터 목록 로드 오류:', error);
      setError('튜터 목록을 불러오는데 실패했습니다.');
      setTutors([]);
      setAllTutors([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };



  // 단일 튜터 등록
  const handleAddTutor = async () => {
    if (!newTutor.name.trim() || !newTutor.email.trim()) {
      await showError('이름과 이메일은 필수 입력 항목입니다.', '입력 오류');
      return;
    }

    try {
      const tutorData = {
        ...newTutor,
        password: '', // 빈 값으로 설정 (최초 로그인 시 설정)
        phone: newTutor.phone || ''
      };

      // 실제로 Firebase에 저장
      const tutorId = await addTutor(tutorData);
      console.log('새 튜터 등록 완료:', tutorId);
      
      setShowAddModal(false);
      setNewTutor({
        name: '',
        email: '',
        phone: '',
        hasCompetition: false
      });
      
      await showSuccess(`${tutorData.name} 튜터가 성공적으로 등록되었습니다!`);
      loadTutors(); // 목록 새로고침
    } catch (error) {
      console.error('튜터 등록 오류:', error);
      await showError('튜터 등록에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 일괄 등록
  const handleBatchAdd = async () => {
    const { names, emails, phones, competitions = '' } = batchData;
    
    if (!names.trim() || !emails.trim()) {
      await showError('이름과 이메일은 필수 입력 항목입니다.', '입력 오류');
      return;
    }

    try {
      // 각 필드를 줄바꿈이나 띄어쓰기로 분리
      const nameList = (names || '').trim().split(/[\s\n]+/).filter(item => item.trim());
      const emailList = (emails || '').trim().split(/[\s\n]+/).filter(item => item.trim());
      const phoneList = (phones || '').trim() ? (phones || '').trim().split(/[\s\n]+/).filter(item => item.trim()) : [];
      const competitionList = (competitions || '').trim() ? (competitions || '').trim().split(/[\s\n]+/).filter(item => item.trim()) : [];

      // 개수 검증
      if (nameList.length !== emailList.length) {
        await showError(`이름 개수(${nameList.length}개)와 이메일 개수(${emailList.length}개)가 일치하지 않습니다.`, '개수 불일치');
        return;
      }

      if (phoneList.length > 0 && phoneList.length !== nameList.length) {
        await showError(`전화번호 개수(${phoneList.length}개)가 이름 개수(${nameList.length}개)와 일치하지 않습니다.\n빈 값은 공백으로 입력해주세요.`, '개수 불일치');
        return;
      }

      // LXP ID는 더 이상 사용하지 않음

      if (competitionList.length > 0 && competitionList.length !== nameList.length) {
        await showError(`경시 이력 개수(${competitionList.length}개)가 이름 개수(${nameList.length}개)와 일치하지 않습니다.\n빈 값은 공백으로 입력해주세요.`, '개수 불일치');
        return;
      }

      const tutorsToAdd = [];

      for (let i = 0; i < nameList.length; i++) {
        const name = nameList[i].trim();
        const email = emailList[i].trim();
        const phone = phoneList[i] ? formatPhoneNumber(phoneList[i].trim()) : '';
        const lxpId = '';
        const competition = competitionList[i] ? competitionList[i].trim().toUpperCase() : 'N';

        if (!name || !email) {
          await showError(`${i + 1}번째 항목에 필수 정보(이름, 이메일)가 누락되었습니다.`, '정보 누락');
          return;
        }

        // 이메일 형식 간단 검증
        if (!email.includes('@')) {
          await showError(`${i + 1}번째 이메일 형식이 잘못되었습니다: ${email}`, '이메일 형식 오류');
          return;
        }

        tutorsToAdd.push({
          name: name,
          email: email,
          password: '', // 빈 비밀번호로 설정 (최초 로그인 시 설정)
          phone: phone,
          hasCompetition: competition === 'Y'
        });
      }

      // 실제로 Firebase에 일괄 저장
      console.log('일괄 등록할 튜터들:', tutorsToAdd);
      
      // Firestore에 일괄 저장
      await addTutorsBatch(tutorsToAdd);
      
      setShowBatchModal(false);
      setBatchData({
        names: '',
        emails: '',
        phones: '',
        competitions: ''
      });
      await showSuccess(`${tutorsToAdd.length}명의 튜터가 일괄 등록되었습니다!`);
      loadTutors(); // 목록 새로고침

    } catch (error) {
      await showError('일괄 등록 중 오류가 발생했습니다.');
      console.error('Batch add error:', error);
    }
  };

  // 튜터 수정
  const handleEditTutor = (tutor) => {
    setSelectedTutor({ ...tutor });
    setShowEditModal(true);
  };

  // 튜터 수정 저장
  const handleSaveEdit = async () => {
    if (!selectedTutor.name.trim() || !selectedTutor.email.trim()) {
      await showError('이름, 코더랜드 이메일은 필수 입력 항목입니다.', '입력 오류');
      return;
    }

    try {
      // 실제로 Firebase에서 업데이트
      const { id, createdAt, ...updateData } = selectedTutor; // id와 createdAt 제외
      await updateTutor(id, updateData);
      
      setShowEditModal(false);
      setSelectedTutor(null);
      await showSuccess('튜터 정보가 수정되었습니다!');
      loadTutors(); // 목록 새로고침
    } catch (error) {
      console.error('튜터 수정 오류:', error);
      await showError('튜터 정보 수정에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 튜터 삭제
  const handleDeleteTutor = async (tutor) => {
    const confirmed = await showConfirm(
      `정말로 "${tutor.name}" 튜터를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`,
      '튜터 삭제',
      '삭제',
      '취소'
    );
    
    if (confirmed) {
      try {
        setDeletingId(tutor.id);
        // 실제로 Firebase에서 튜터 및 연관 데이터(스케줄/메모)까지 삭제
        await deleteTutorCascade(tutor.id);
        console.log('튜터 삭제 완료:', tutor.id);
        await showSuccess('튜터가 삭제되었습니다!');
        
        // 현재 사용자가 삭제된 경우 로그아웃 처리
        if (tutor.id === user?.id) {
          alert('자신의 계정이 삭제되어 로그아웃됩니다.');
          // 부모 컴포넌트에서 로그아웃 처리
          window.location.href = '/';
          return;
        }
        
        loadTutors(); // 목록 새로고침
      } catch (error) {
        console.error('튜터 삭제 오류:', error);
        await showError('튜터 삭제에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setDeletingId(null);
      }
    }
  };

  // 페이지 변경
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 검색 핸들러
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setCurrentPage(1); // 검색 시 첫 페이지로 이동
  };

  // 검색어 변경 시 튜터 목록 다시 로드
  useEffect(() => {
    loadTutors();
  }, [searchTerm, currentPage]);



  return (
    <div className="tutor-management">
      <div className="page-header">
        <h1>👨‍🏫 튜터 관리</h1>
                 <div className="header-actions">
           <button 
             className="btn-add" 
             onClick={() => setShowAddModal(true)}
           >
             ➕ 튜터 등록
           </button>
           <button 
             className="btn-batch" 
             onClick={() => setShowBatchModal(true)}
           >
             📝 일괄 등록
           </button>
         </div>
      </div>

      {/* 검색 섹션 */}
      <div className="search-section">
        <div className="search-container">
          <input
            type="text"
            placeholder="🔍 튜터명, 코더랜드 이메일로 검색..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
          {searchTerm && (
            <span className="search-count">
              {allTutors.filter(tutor => 
                tutor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tutor.email.toLowerCase().includes(searchTerm.toLowerCase())
              ).length}명 검색됨
            </span>
          )}
        </div>
      </div>

      {/* 튜터 목록 테이블 */}
      <div className="tutors-container">
        {error ? (
          <div className="error-message">{error}</div>
        ) : (
          <>
            <div className="tutors-table">
              <div className="table-header">
                <div className="header-cell">No.</div>
                <div className="header-cell">튜터명</div>
                <div className="header-cell">코더랜드 이메일</div>
                <div className="header-cell">비밀번호</div>
                <div className="header-cell">전화번호</div>
                <div className="header-cell">경시</div>
                <div className="header-cell">임시 비밀번호</div>
                <div className="header-cell">관리</div>
              </div>

              {tutors.map((tutor, index) => (
                <div key={tutor.id} className="table-row">
                  <div className="table-cell">{(currentPage - 1) * tutorsPerPage + index + 1}</div>
                  <div className="table-cell tutor-name">{tutor.name}</div>
                  <div className="table-cell tutor-email">{tutor.email}</div>
                  <div className="table-cell password-cell">{tutor.password || '-'}</div>
                  <div className="table-cell">{tutor.phone || '-'}</div>
                  <div className="table-cell">
                    {tutor.hasCompetition ? (
                      <span className="competition-badge">🏆 경시</span>
                    ) : '-'}
                  </div>
                  <div className="table-cell password-cell">
                    <button
                      className="btn-edit"
                      onClick={async () => {
                        try {
                          const confirmed = await showConfirm('선택한 튜터의 임시 비밀번호를 재발급할까요?\n재발급 즉시 로그인 가능하며, 다음 로그인 시 비밀번호 변경이 요구됩니다.', '임시 비밀번호 재발급', '재발급', '취소');
                          if (!confirmed) return;
                          const temp = await issueTemporaryPassword(tutor.id);
                          await showSuccess(`임시 비밀번호가 재발급되었습니다.\n${tutor.name} (${tutor.email})\n임시 비밀번호: ${temp}`);
                          loadTutors();
                        } catch (e) {
                          console.error(e);
                          await showError('임시 비밀번호 재발급에 실패했습니다.');
                        }
                      }}
                    >
                      임시 비밀번호 재발급
                    </button>
                  </div>
                  <div className="table-cell actions-cell">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEditTutor(tutor)}
                    >
                      수정
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeleteTutor(tutor)}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              ))}

              {tutors.length === 0 && (
                <div className="empty-state">
                  <p>등록된 튜터가 없습니다.</p>
                </div>
              )}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="page-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ◀
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`page-btn ${page === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                
                <button 
                  className="page-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  ▶
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 단일 튜터 등록 모달 */}
      {showAddModal && (
        <AddTutorModal
          tutor={newTutor}
          setTutor={setNewTutor}
          onSave={handleAddTutor}
          onClose={() => {
            setShowAddModal(false);
            setNewTutor({
              name: '',
              email: '',
              password: '',
              phone: '',
              hasCompetition: false
            });
          }}
        />
      )}

      {/* 일괄 등록 모달 */}
      {showBatchModal && (
        <BatchAddModal
          data={batchData}
          setData={setBatchData}
          onSave={handleBatchAdd}
          onClose={() => {
            setShowBatchModal(false);
            setBatchData({
              names: '',
              emails: '',
              phones: '',
              competitions: ''
            });
          }}
        />
      )}

      {/* 튜터 수정 모달 */}
      {showEditModal && selectedTutor && (
        <EditTutorModal
          tutor={selectedTutor}
          setTutor={setSelectedTutor}
          onSave={handleSaveEdit}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTutor(null);
          }}
        />
      )}

      {/* 커스텀 알림 모달 */}
      <CustomAlert {...alertState} />

      {/* 전역 삭제 진행 오버레이 */}
      {deletingId && (
        <div className="global-deleting-overlay" role="status" aria-live="polite">
          <div className="global-deleting-box">
            <div className="loading-spinner-lg" />
            <div className="deleting-text">튜터 관련 데이터(계정, 스케줄, 메모) 삭제 중...</div>
          </div>
        </div>
      )}
    </div>
  );
};

// 단일 튜터 등록 모달
const AddTutorModal = ({ tutor, setTutor, onSave, onClose }) => {
  // 전화번호 포맷팅 함수 (010-1234-5678 형식으로 변환)
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '');
    
    // 11자리 숫자인 경우에만 포맷팅
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    
    // 10자리 숫자인 경우 (010으로 시작하는 경우)
    if (numbers.length === 10 && numbers.startsWith('010')) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    
    // 그 외의 경우는 원본 반환
    return phone;
  };

  const handleOverlayClick = (e) => {
    // 드래그 중이 아닐 때만 모달 닫기
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>➕ 새 튜터 등록</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="tutor-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">👨‍🏫 튜터명 *</label>
                <input
                  id="name"
                  type="text"
                  placeholder="튜터명을 입력하세요"
                  value={tutor.name}
                  onChange={(e) => setTutor(prev => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">📧 코더랜드 이메일 *</label>
                <input
                  id="email"
                  type="email"
                  placeholder="이메일을 입력하세요"
                  value={tutor.email}
                  onChange={(e) => setTutor(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>



            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">📞 전화번호</label>
                <input
                  id="phone"
                  type="text"
                  placeholder="010-1234-5678 (선택사항)"
                  value={tutor.phone}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    setTutor(prev => ({ ...prev, phone: formatted }));
                  }}
                />
              </div>
              
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={tutor.hasCompetition}
                    onChange={(e) => setTutor(prev => ({ ...prev, hasCompetition: e.target.checked }))}
                  />
                  <span className="checkmark"></span>
                  🏆 경시 이력
                </label>
                <div className="checkbox-description">
                  경시대회 수상 이력이 있는 튜터인 경우 체크해주세요.
                </div>
              </div>
            </div>
          </div>

          <div className="form-note">
            * 표시된 항목은 필수 입력 사항입니다.<br/>
            비밀번호는 튜터가 최초 로그인 시 설정합니다.
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>
            취소
          </button>
          <button 
            className="modal-confirm-btn" 
            onClick={onSave}
            disabled={!tutor.name.trim() || !tutor.email.trim()}
          >
            ➕ 등록
          </button>
        </div>
      </div>
    </div>
  );
};

// 일괄 등록 모달
const BatchAddModal = ({ data, setData, onSave, onClose }) => {
  const handleOverlayClick = (e) => {
    // 드래그 중이 아닐 때만 모달 닫기
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content batch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📝 튜터 일괄 등록</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="batch-instructions">
            <h4>📋 입력 방법</h4>
            <p>각 섹션에 데이터를 띄어쓰기 또는 줄바꿈으로 구분하여 입력해주세요</p>
            <p className="note">* 이름과 코더랜드 이메일은 필수입니다</p>
            <p className="note">* 비밀번호는 튜터가 최초 로그인 시 설정합니다</p>
            <p className="note">* 선택 항목에서 빈 값은 공백(' ')으로 입력해주세요</p>
          </div>

          <div className="batch-sections">
            <div className="section-row">
              <div className="form-group">
                <label htmlFor="names">👨‍🏫 이름 *</label>
                <textarea
                  id="names"
                  placeholder="강경호 김민지 박철수"
                  value={data.names}
                  onChange={(e) => setData(prev => ({ ...prev, names: e.target.value }))}
                  rows={4}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="emails">📧 코더랜드 이메일 *</label>
                <textarea
                  id="emails"
                  placeholder="kangkh@gmail.com&#10;kim@naver.com&#10;park@example.com"
                  value={data.emails}
                  onChange={(e) => setData(prev => ({ ...prev, emails: e.target.value }))}
                  rows={4}
                />
              </div>
            </div>

            <div className="section-row">
              <div className="form-group">
                <label htmlFor="phones">📞 전화번호</label>
                <textarea
                  id="phones"
                  placeholder="010-1234-5678&#10;010-2345-6789&#10; "
                  value={data.phones}
                  onChange={(e) => setData(prev => ({ ...prev, phones: e.target.value }))}
                  rows={4}
                />
                <div className="input-description">
                  하이픈(-) 없이 입력해도 자동으로 포맷팅됩니다 (예: 01012345678 → 010-1234-5678)
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="competitions">🏆 경시 이력</label>
                <textarea
                  id="competitions"
                  placeholder="Y&#10;N&#10;Y"
                  value={data.competitions}
                  onChange={(e) => setData(prev => ({ ...prev, competitions: e.target.value }))}
                  rows={4}
                />
                <div className="input-description">
                  Y: 경시 이력 있음, N: 없음 (각 튜터별로 Y 또는 N 입력)
                </div>
              </div>
            </div>
          </div>

          <div className="batch-tips">
            <h4>💡 입력 팁</h4>
            <ul>
              <li>엑셀에서 열 단위로 복사하여 붙여넣기 가능</li>
              <li>각 섹션의 항목 수는 동일해야 합니다</li>
              <li>선택 항목에서 빈 값은 공백으로 입력</li>
              <li>로그인: 코더랜드 이메일 사용</li>
              <li>비밀번호: 튜터가 최초 로그인 시 직접 설정</li>
            </ul>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>
            취소
          </button>
          <button 
            className="modal-confirm-btn" 
            onClick={onSave}
            disabled={!data.names.trim() || !data.emails.trim()}
          >
            📝 일괄 등록
          </button>
        </div>
      </div>
    </div>
  );
};

// 튜터 수정 모달
const EditTutorModal = ({ tutor, setTutor, onSave, onClose }) => {
  // 전화번호 포맷팅 함수 (010-1234-5678 형식으로 변환)
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '');
    
    // 11자리 숫자인 경우에만 포맷팅
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    
    // 10자리 숫자인 경우 (010으로 시작하는 경우)
    if (numbers.length === 10 && numbers.startsWith('010')) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    
    // 그 외의 경우는 원본 반환
    return phone;
  };

  const handleOverlayClick = (e) => {
    // 드래그 중이 아닐 때만 모달 닫기
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>✏️ 튜터 정보 수정</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="tutor-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-name">👨‍🏫 튜터명 *</label>
                <input
                  id="edit-name"
                  type="text"
                  placeholder="튜터명을 입력하세요"
                  value={tutor.name}
                  onChange={(e) => setTutor(prev => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-email">📧 코더랜드 이메일 *</label>
                <input
                  id="edit-email"
                  type="email"
                  placeholder="이메일을 입력하세요"
                  value={tutor.email}
                  onChange={(e) => setTutor(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>

            
            <div className="form-group">
              <label htmlFor="edit-password">🔐 비밀번호</label>
              <input
                id="edit-password"
                type="password"
                placeholder="새 비밀번호 (변경하지 않으려면 비워두세요)"
                value={tutor.password === '••••••••' ? '' : tutor.password}
                onChange={(e) => setTutor(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="edit-phone">📞 전화번호</label>
              <input
                id="edit-phone"
                type="text"
                placeholder="010-1234-5678 (선택사항)"
                value={tutor.phone}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setTutor(prev => ({ ...prev, phone: formatted }));
                }}
              />
            </div>
            
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={tutor.hasCompetition || false}
                  onChange={(e) => setTutor(prev => ({ ...prev, hasCompetition: e.target.checked }))}
                />
                <span className="checkmark"></span>
                🏆 경시 이력
              </label>
              <div className="checkbox-description">
                경시대회 수상 이력이 있는 튜터인 경우 체크해주세요.
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>
            취소
          </button>
          <button 
            className="modal-confirm-btn" 
            onClick={onSave}
            disabled={!tutor.name.trim() || !tutor.email.trim()}
          >
            ✅ 저장
          </button>
        </div>
      </div>

    </div>
  );
};



export default TutorManagement; 